import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getToken } from './api.js'
import { useAuth } from './auth.jsx'

/**
 * The live feed.
 *
 * The API pushes `{topic, action}` pings over SSE; screens turn those into a
 * silent re-fetch of whatever they already show. We read the stream with
 * `fetch` rather than `EventSource` so the JWT rides in an Authorization
 * header instead of the query string, where it would end up in every proxy log.
 */

const LiveContext = createContext(null)

const BACKOFF = [1000, 2000, 4000, 8000, 15000, 30000]

export function LiveProvider ({ children }) {
  const { user } = useAuth()
  const [status, setStatus] = useState('idle') // idle | connecting | live | retrying
  const [presence, setPresence] = useState({ count: 0, people: [] })
  const [versions, setVersions] = useState({})
  const [lastEvent, setLastEvent] = useState(null)

  const handlers = useRef(new Set())
  const attempt = useRef(0)

  const onChange = useCallback(payload => {
    setLastEvent(payload)
    setVersions(v => ({ ...v, [payload.topic]: (v[payload.topic] || 0) + 1 }))
    for (const fn of handlers.current) {
      try { fn(payload) } catch { /* a bad subscriber must not kill the feed */ }
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setStatus('idle')
      setPresence({ count: 0, people: [] })
      return
    }

    const controller = new AbortController()
    let stopped = false
    let retryTimer = null

    const connect = async () => {
      if (stopped) return
      setStatus(attempt.current ? 'retrying' : 'connecting')
      try {
        const res = await fetch('/api/stream', {
          headers: { Authorization: `Bearer ${getToken()}`, Accept: 'text/event-stream' },
          signal: controller.signal,
          cache: 'no-store'
        })
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`)

        attempt.current = 0
        setStatus('live')

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        for (;;) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })

          // Frames are separated by a blank line; keep the tail for next chunk.
          let split
          while ((split = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, split)
            buffer = buffer.slice(split + 2)
            handleFrame(frame)
          }
        }
        throw new Error('stream closed')
      } catch (err) {
        if (stopped || controller.signal.aborted) return
        const wait = BACKOFF[Math.min(attempt.current++, BACKOFF.length - 1)]
        setStatus('retrying')
        retryTimer = setTimeout(connect, wait)
      }
    }

    const handleFrame = frame => {
      let event = 'message'
      const data = []
      for (const line of frame.split('\n')) {
        if (line.startsWith(':')) continue
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data.push(line.slice(5).trim())
      }
      if (!data.length) return
      let payload
      try { payload = JSON.parse(data.join('\n')) } catch { return }

      if (event === 'presence') setPresence({ count: payload.count || 0, people: payload.people || [] })
      else if (event === 'change') onChange(payload)
      else if (event === 'ready') setStatus('live')
    }

    connect()
    return () => {
      stopped = true
      clearTimeout(retryTimer)
      controller.abort()
    }
  }, [user, onChange])

  const subscribe = useCallback(fn => {
    handlers.current.add(fn)
    return () => handlers.current.delete(fn)
  }, [])

  const value = useMemo(
    () => ({ status, presence, versions, lastEvent, subscribe }),
    [status, presence, versions, lastEvent, subscribe]
  )
  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>
}

export const useLive = () => useContext(LiveContext) || { status: 'idle', presence: { count: 0, people: [] }, versions: {}, subscribe: () => () => {} }

/**
 * A number that ticks every time one of `topics` changes on the server.
 * Feed it into a dependency list and the screen refreshes itself.
 */
export function useLiveVersion (topics) {
  const { versions } = useLive()
  const list = Array.isArray(topics) ? topics : [topics].filter(Boolean)
  return list.reduce((sum, t) => sum + (versions[t] || 0), 0)
}

const NOUN = {
  enquiries: 'Enquiry',
  orders: 'Booking',
  batches: 'Batch',
  hatcheries: 'Hatchery',
  farmers: 'Farmer',
  trips: 'Trip',
  users: 'Login',
  network: 'Transport record',
  settings: 'Settings'
}

/** "Enquiry added" / "Booking sample recorded" — what to put on a live banner. */
export function describeEvent (e) {
  if (!e) return ''
  const noun = NOUN[e.topic] || e.topic
  const verb = e.action === 'created'
    ? 'added'
    : e.action === 'changed'
      ? 'updated'
      : e.action.replace(/[-_]/g, ' ')
  return `${noun} ${verb}`
}

/** Run a callback on matching pings — used for desk toasts. */
export function useLiveEvent (topics, handler) {
  const { subscribe } = useLive()
  const list = Array.isArray(topics) ? topics : [topics].filter(Boolean)
  const ref = useRef(handler)
  ref.current = handler
  const key = list.join(',')

  useEffect(() => {
    return subscribe(payload => {
      if (!key || key.split(',').includes(payload.topic)) ref.current?.(payload)
    })
  }, [subscribe, key])
}
